import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Command } from 'cmdk';
import {
  LayoutDashboard,
  Store,
  FolderTree,
  Package,
  ShoppingCart,
  CreditCard,
  Wallet,
  BarChart3,
  Settings,
  Search,
  ArrowRight,
  ImageIcon,
} from 'lucide-react';

const commands = [
  { id: 'dashboard',    label: 'Dashboard',    path: '/dashboard',    icon: LayoutDashboard, group: 'Pages' },
  { id: 'stores',       label: 'Stores',       path: '/stores',       icon: Store,           group: 'Pages' },
  { id: 'categories',   label: 'Categories',   path: '/categories',   icon: FolderTree,      group: 'Pages' },
  { id: 'products',     label: 'Products',     path: '/products',     icon: Package,         group: 'Pages' },
  { id: 'banners',      label: 'Banners',      path: '/banners',      icon: ImageIcon,       group: 'Pages' },
  { id: 'orders',       label: 'Orders',       path: '/orders',       icon: ShoppingCart,    group: 'Pages' },
  { id: 'payments',     label: 'Payments',     path: '/payments',     icon: CreditCard,      group: 'Finance' },
  { id: 'wallet-dues',  label: 'Wallet Dues',  path: '/wallet-dues',  icon: Wallet,          group: 'Finance' },
  { id: 'reports',      label: 'Reports',      path: '/reports',      icon: BarChart3,       group: 'Insights' },
  { id: 'settings',     label: 'Settings',     path: '/settings',     icon: Settings,        group: 'Account' },
  { id: 'new-store',    label: 'Add New Store',    path: '/stores/new',    icon: Store,    group: 'Actions' },
  { id: 'new-product',  label: 'Add New Product',  path: '/products/new',  icon: Package,  group: 'Actions' },
];

const CommandPalette = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const handleSelect = useCallback((path) => {
    navigate(path);
    onClose();
  }, [navigate, onClose]);

  if (!open) return null;

  const groups = [...new Set(commands.map(c => c.group))];

  const filtered = query
    ? commands.filter(c => c.label.toLowerCase().includes(query.toLowerCase()))
    : commands;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[100] animate-fade-in"
        onClick={onClose}
      />

      {/* Palette */}
      <div className="fixed inset-x-0 top-[20vh] z-[101] flex justify-center px-4 animate-scale-in">
        <div
          className="w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden"
          style={{
            background: 'rgba(255,255,255,0.98)',
            border: '1px solid rgba(139,92,246,0.15)',
            backdropFilter: 'blur(20px)',
          }}
        >
          <Command shouldFilter={false}>
            {/* Search input */}
            <div className="flex items-center gap-3 px-4 py-3.5 border-b border-slate-100">
              <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <Command.Input
                value={query}
                onValueChange={setQuery}
                placeholder="Search pages and actions…"
                className="flex-1 text-sm text-slate-900 placeholder:text-slate-400 outline-none bg-transparent"
                autoFocus
              />
              <kbd className="text-[10px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded font-mono">ESC</kbd>
            </div>

            <Command.List className="max-h-72 overflow-y-auto py-2">
              <Command.Empty className="py-8 text-center text-sm text-slate-400">
                No results for &ldquo;{query}&rdquo;
              </Command.Empty>

              {query ? (
                <Command.Group>
                  {filtered.map((cmd) => {
                    const Icon = cmd.icon;
                    return (
                      <Command.Item
                        key={cmd.id}
                        value={cmd.id}
                        onSelect={() => handleSelect(cmd.path)}
                        className="flex items-center gap-3 px-4 py-2.5 mx-2 rounded-xl cursor-pointer
                                   text-sm text-slate-700 hover:bg-purple-50 hover:text-purple-900
                                   aria-selected:bg-purple-50 aria-selected:text-purple-900
                                   transition-colors group"
                      >
                        <div className="w-7 h-7 rounded-lg bg-slate-100 group-aria-selected:bg-purple-100
                                        flex items-center justify-center flex-shrink-0 transition-colors">
                          <Icon className="w-3.5 h-3.5 text-slate-500 group-aria-selected:text-purple-700" />
                        </div>
                        <span className="flex-1 font-medium">{cmd.label}</span>
                        <ArrowRight className="w-3.5 h-3.5 text-slate-300 group-aria-selected:text-purple-400" />
                      </Command.Item>
                    );
                  })}
                </Command.Group>
              ) : (
                groups.map((group) => (
                  <Command.Group
                    key={group}
                    heading={
                      <span className="px-4 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                        {group}
                      </span>
                    }
                  >
                    {commands.filter(c => c.group === group).map((cmd) => {
                      const Icon = cmd.icon;
                      return (
                        <Command.Item
                          key={cmd.id}
                          value={cmd.id}
                          onSelect={() => handleSelect(cmd.path)}
                          className="flex items-center gap-3 px-4 py-2.5 mx-2 rounded-xl cursor-pointer
                                     text-sm text-slate-700 hover:bg-purple-50 hover:text-purple-900
                                     aria-selected:bg-purple-50 aria-selected:text-purple-900
                                     transition-colors group"
                        >
                          <div className="w-7 h-7 rounded-lg bg-slate-100 group-aria-selected:bg-purple-100
                                          flex items-center justify-center flex-shrink-0 transition-colors">
                            <Icon className="w-3.5 h-3.5 text-slate-500 group-aria-selected:text-purple-700" />
                          </div>
                          <span className="flex-1 font-medium">{cmd.label}</span>
                          <ArrowRight className="w-3.5 h-3.5 text-slate-300 group-aria-selected:text-purple-400" />
                        </Command.Item>
                      );
                    })}
                  </Command.Group>
                ))
              )}
            </Command.List>

            {/* Footer hint */}
            <div className="px-4 py-2.5 border-t border-slate-100 flex items-center gap-4 text-[10px] text-slate-400">
              <span><kbd className="font-mono bg-slate-100 px-1 py-0.5 rounded">↑↓</kbd> navigate</span>
              <span><kbd className="font-mono bg-slate-100 px-1 py-0.5 rounded">↵</kbd> select</span>
              <span><kbd className="font-mono bg-slate-100 px-1 py-0.5 rounded">ESC</kbd> close</span>
            </div>
          </Command>
        </div>
      </div>
    </>
  );
};

export default CommandPalette;
